<template>
    <div class="page-wrapper page">
        <game-animations v-if="!!shotsTaken" :shotsTaken="shotsTaken" @close="shotsTaken = 0;"></game-animations>

        <div v-if="currentGame" class="game-wrapper">
            <div class="center" :class="{ 'move-up': showScorePopup }">
                <div ref="gameContainer" class="game-container" @scroll.passive="_handleContainerScroll">
                    <div class="overflow-container">
                        <div class="users-container">
                            <draggable 
                                v-model="roundUsers"
                                handle=".enable-drag"
                                @change="handleEmitOrderChange" 
                                @start="dragging = true"
                                @end="dragging = false;"
                            >
                                <transition-group class="users-wrapper">
                                    <user-circle 
                                        v-for="user in roundUsers" 
                                        :key="user.id"
                                        class="grid-item" 
                                        :class="{ 'enable-drag': enableDragUsers }"
                                        :user="user"
                                        :roundScore="userRoundScores[user.id]"
                                        :focus="editScoreUserId === user.id"
                                        :staggerPopping="enableDragUsers && !dragging"
                                        @editUser="toggleEditUserScore"
                                    ></user-circle>
                                </transition-group>
                            </draggable>
                        </div>
                    </div>
                </div>
            </div>
            <div class="bottom">
                <div v-if="!enableDragUsers" class="rounds-container">
                    <div ref="RoundsContainer" class="rounds-scroll-bar" @scroll="_handleScroll">
                        <div
                            v-for="round in currentGame.totalRounds" 
                            :key="round" :id="`round-${round}`" 
                            class="round" 
                            :class="{ active: currentRoundNumber === round }" 
                            @click="handleRoundSelected(round)">
                            <p>Ronde {{ round }}</p>
                        </div>
                    </div>
                    <div class="line"></div>
                </div>
                <jump-transition>
                    <div v-if="enableDragUsers" class="button-container">
                        <custom-button @click="handleUserDragFinished">Volgorde gebruiken</custom-button>
                    </div>
                </jump-transition>
            </div>
            <div ref="scorePopup" class="add-score-popup" :class="{ show: showScorePopup }">
                <div class="input-field">
                    <div ref="calculation" class="custom-input">{{ editingUserRoundScore }}</div>
                    <custom-button
                        size="large"
                        variation="grey"
                        class="backspace-button"
                        @click="handleBackspace"
                    >
                    <svg class="backspace-icon" viewBox="0 0 512 512"><path d="M403.13 96H156.87a44.9 44.9 0 00-33.68 15.27 15.88 15.88 0 00-1.91 2.7L32 247.75a16 16 0 000 16.5l89.15 133.57a16.24 16.24 0 002 2.88 44.89 44.89 0 0033.7 15.3h246.28A44.92 44.92 0 00448 371.13V140.87A44.92 44.92 0 00403.13 96zM348 311a16 16 0 11-22.63 22.62L271.67 280 218 333.65A16 16 0 01195.35 311L249 257.33l-53.69-53.69A16 16 0 01218 181l53.69 53.7 53.67-53.7A16 16 0 01348 203.64l-53.7 53.69z"/></svg>
                    </custom-button>
                </div>
                <div class="inputs-wrapper flex-column-gap">
                    <div class="row">
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(1)"
                            >1</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(2)"
                            >2</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(3)"
                            >3</custom-button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(4)"
                            >4</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(5)"
                            >5</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(6)"
                            >6</custom-button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(7)"
                            >7</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(8)"
                            >8</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                class="number-button"
                                :disabled="currentRound.scores.length === 0"
                                @click="handleInput(9)"
                            >9</custom-button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="item">
                            <custom-button
                                size="large"
                                variation="grey"
                                class="icon-button"
                                @click="handleEditPreviousUser"
                            >
                                <svg class="icon previous" :class="{ 'rotate-down': canStartNextRound }" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M328 112L184 256l144 144"/></svg>
                            </custom-button>
                        </div>
                        <div class="item">
                            <custom-select 
                                v-if="!editingUserRoundScore && currentRound.scores.length === 0" 
                                class="select-button" 
                                :options="victoryOptions"
                                @input="handleUserVictory(editScoreUserId, $event)"
                                size="large"
                                direction="up"
                                selectionType="buttons"
                            >JP</custom-select>
                            <custom-button
                                v-else
                                size="large"
                                class="number-button"
                                @click="handleInput(0)"
                            >0</custom-button>
                        </div>
                        <div class="item">
                            <custom-button
                                size="large"
                                variation="grey"
                                class="icon-button"
                                @click="handleEditNextUser"
                            >
                                <svg class="icon next" :class="{ 'rotate-down': canStartNextRound }" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M184 112l144 144-144 144"/></svg>
                            </custom-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';

import CustomButton from '@/components/CustomButton.vue';
import CustomSelect from '@/components/CustomSelect.vue';
import JumpTransition from '@/components/transitions/JumpTransition.vue';
import UserCircle from '@/components/UserCircle.vue';
import GameAnimations from '@/components/GameAnimations.vue';

export default {
    name: 'dashboard',
    components: {
        CustomButton,
        CustomSelect,
        JumpTransition,
        draggable,
        UserCircle,
        GameAnimations,
    },
    props: {
        game: {
            type: Object,
            default() { return null }
        },
        users: {
            type: Array,
            default() { return [] }
        },
        hasChanges: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            currentGame: null,
            configuringGame: null,
            configuringNewGame: false,
            selectGame: false,
            isPlayingGame: false,
            editScoreUserId: null,
            handleScrollDisabled: false,
            infoMessage: 'Wie heeft er als eerst JP?',
            resetCheckpoints: [50, 69, 75, 100],
            shotCheckpoints: [25, 50, 75, 100],
            socket: null,
            currentRoundNumber: undefined,
            gameUsers: [],
            showScorePopup: false,
            hasNavigator: false,
            lastScrollTop: 0,
            victoryOptions: [
                {
                    label: 0,
                    value: 0
                },
                {
                    label: 1,
                    value: 1
                },
                {
                    label: 2,
                    value: 2
                },
                {
                    label: 3,
                    value: 3
                },
                {
                    label: 4,
                    value: 4
                },
                {
                    label: 5,
                    value: 5
                },
            ],
            enableDragUsers: false,
            scrolling: false,
            isInLastRound: true,
            userRoundScores: {},
            dragging: false,
            cachedRounds: [],
            roundUsers: [],
            currentRound: { scores: [] },
            shotsTaken: 0,
        }
    },
    methods: {
        startGame(game) {
            this.currentGame = game;
            this.isPlayingGame = true;

            const rounds = this.currentGame.rounds || [];
            this.cachedRounds = rounds;
            this.currentRoundNumber = rounds.length;

            this.setCurrentRound();
            this.setRoundUsers();
            
            this.startRound();
            this.scrollToRound(this.currentGame.totalRounds, true);
        },
        async scrollToRound(round, initialize, wait = 0) {
            if (wait) await new Promise(r => setTimeout(r, wait));
            
            this.$nextTick(() => {
                this.disableHandleScroll();
                this.currentRoundNumber = round;
                const scrollContainer = this.$refs.RoundsContainer;
                if(initialize) scrollContainer.style.scrollBehavior = 'auto';
                const roundWidthWithGap = 120;
                const offset = roundWidthWithGap * (round-1);
                this.$refs.RoundsContainer.scrollLeft = offset;
                if(initialize) scrollContainer.style.scrollBehavior = 'smooth';
            })
        },
        async disableHandleScroll(duration = 250) {
            this.handleScrollDisabled = true;
            await new Promise(r => setTimeout(r, duration));
            this.handleScrollDisabled = false;
        },
        startRound() {
            this.createNewRound();
            this.currentRoundNumber = this.currentGame.rounds.length;
            this.setCurrentRound();
            this.recalculateUsersDisplayScores();
            this.setRoundUsers();
            this.setAllCircles();
            this.setUserRoundScores();
            this.editScoreUserId = null;
            this.showScorePopup = false;
        },
        createNewRound(force = false) {
            if ((this.currentGame.rounds.length !== 0 || !this.canStartNextRound) && !force) return;

            const userIds = this.currentRound.userIds;
            const users = userIds.map(userId => this.currentGame.users.find(user => user.id === userId));

            const newRound = {
                scores: [],
                userIds,
                users
            }
            this.currentGame.rounds.push(newRound);
            this.currentGame.totalRounds = this.currentGame.rounds.length;
            console.log('createnewround')
            this.handleGameUpdated();
        },
        cancelConfiguringGame() {
            this.configureNewGame = false;
            this.configuringGame = null;
        },
        cancelSelectingGame() {
            this.selectGame = false;
        },
        handleGameUpdated() {
            this.$emit('updateGame', this.currentGame);
        },
        handleEditNextUser() {
            if(this.canStartNextRound) return this.handleFinishRound()

            const currentUserId = this.editScoreUserId || this.roundUsers[0].id;
            const index = this.roundUsers.findIndex(user => user.id === currentUserId);
            let newIndex = index+1;
            if(newIndex > this.roundUsers.length-1) newIndex = 0;

            const nextUserId = this.roundUsers[newIndex].id;
            this.toggleEditUserScore(nextUserId);
            
        },
        handleEditPreviousUser() {
            if(this.canStartNextRound) return this.handleFinishRound()

            const currentUserId = this.editScoreUserId || this.roundUsers[0].id;
            const index = this.roundUsers.findIndex(user => user.id === currentUserId);
            let newIndex = index-1;
            if(newIndex === -1) newIndex = this.roundUsers.length-1;

            const nextUserId = this.roundUsers[newIndex].id;
            this.toggleEditUserScore(nextUserId);
        },
        toggleEditUserScore(userId) {
            if(this.currentRoundNumber < this.lastActiveRound || this.enableDragUsers) return

            let you = localStorage.getItem('you');
            if (!you) return;
            you = JSON.parse(you);
            if (!this.currentGame.userIds.includes(you.id)) return;

            this.scrollUserIntoView(userId);
            this.editScoreUserId = userId;
            this.showScorePopup = true;
        },
        async scrollUserIntoView(userId) {
            await new Promise(r => setTimeout(r, 1));
            const user = document.getElementById(`user-${userId}`);
            const { offsetTop } = user;
            const { height } = user.getBoundingClientRect();
            const scrollContainer = this.$refs.gameContainer;
            const visibleHeight = window.innerHeight - 288;
            const scrollY = offsetTop + (height/2) - (visibleHeight/2) - 40;
            scrollContainer.scrollTo({ top: scrollY, behavior: 'smooth' });
        },
        handleInput(input) {
            let currentInput = JSON.stringify(this.editingUserRoundScore || 0);
            if(!currentInput) currentInput = input;
            else currentInput += input;
            // this.$refs.calculation.innerHTML = currentInput;

            const score = parseInt(currentInput);

            this.updateUserRoundScore(this.editScoreUserId, score);

            this.handleScoreInput();
        },
        handleBackspace() {
            let currentInput = JSON.stringify(this.editingUserRoundScore);
            if(!currentInput) return
            currentInput = currentInput.substring(0, currentInput.length-1) || 0;
            // this.$refs.calculation.innerHTML = currentInput;

            const score = parseInt(currentInput) || null;

            this.updateUserRoundScore(this.editScoreUserId, score);
            this.handleScoreInput();
        },
        handleScoreInput() {
            if(this.currentRoundNumber < this.lastActiveRound) return
            if(!this.editScoreUserId) return

            this.recalculateUserDisplayScore(this.editScoreUserId);
            this.setCircleAnimation(this.editScoreUserId);
            console.log('handlescoreinput')
            this.handleGameUpdated();
        },
        recalculateUsersDisplayScores() {
            for(let i = 0; i < this.currentRound.userIds.length; i++) {
                this.recalculateUserDisplayScore(this.currentRound.userIds[i]);
            }
        },
        recalculateUserDisplayScore(userId) {
            const userIndex = this.currentGame.users.findIndex(user => user.id === userId);
            if(userIndex === -1) return
            
            const roundIndex = this.currentRoundNumber;
            let accumulatedScore = 0;

            for(let i = 0; i < roundIndex; i++) {
                const round = this.currentGame.rounds[i];
                if(!round) continue
                const userRoundScoreIndex = round.scores.findIndex(score => score.userId === userId);
                if(userRoundScoreIndex === -1) continue
                const { score } = round.scores[userRoundScoreIndex];
                accumulatedScore += score;

                if(this.resetCheckpoints.includes(accumulatedScore) && i <= roundIndex-2 && score > 0) {
                    accumulatedScore = this.getResetScore(accumulatedScore);
                }
            }

            const oldDisplayScore = this.currentGame.users[userIndex].displayScore || 0;
            const lastDisplayScore = this.currentGame.users[userIndex].lastDisplayScore;

            this.currentGame.users[userIndex].displayScore = accumulatedScore;
            this.currentGame.users[userIndex].score = accumulatedScore;
            if(oldDisplayScore !== accumulatedScore || lastDisplayScore === undefined) this.currentGame.users[userIndex].lastDisplayScore = oldDisplayScore;

            this.setRoundUsers();
        },
        updateUserRoundScore(userId, score, settings = {}) {
            const roundIndex = this.currentRoundNumber-1;
            const roundScores = this.currentRound.scores;

            settings['index'] = roundScores.length;

            let userRoundScoreIndex = roundScores.findIndex(score => score.userId === userId);
            if(score === null && userRoundScoreIndex !== -1) {
                this.currentGame.rounds[roundIndex].scores = this.currentGame.rounds[roundIndex].scores.filter((s, i) => i !== userRoundScoreIndex);
            } else if(userRoundScoreIndex === -1) {
                this.currentGame.rounds[roundIndex].scores.push({
                    userId,
                    score,
                    settings
                });
            } else {
                this.currentGame.rounds[roundIndex].scores = this.currentGame.rounds[roundIndex].scores.map(roundScore => {
                    if(roundScore.userId === userId) return {
                        userId,
                        score,
                        settings
                    }
                    return roundScore
                });
            }
            this.setCurrentRound();
        },
        handleUserVictory(userId, score) {
            const settings = {};
            if(score === 0) {
                const user = this.roundUsers.find(user => user.id === userId);
                settings['finishWithZero'] = true;
                score = user.displayScore >= 30 ? -30 : user.displayScore * -1;
            }

            this.updateUserRoundScore(userId, score, settings);
            // this.$refs.calculation.innerHTML = score;
            this.handleScoreInput();
            if(this.currentRound.scores.length !== this.roundUsers.length) this.handleEditNextUser();
            else this.handleFinishRound();
        },
        handleFinishRound(passive = false) {
            this.showScorePopup = false;
            this.editScoreUserId = null;
            this.handleRoundFinishedCalculation(this.currentRoundNumber-1);

            if(this.currentRoundNumber === this.currentGame.totalRounds && !passive) this.createNewRound(true);
            this.setUserRoundScores();

            const scrollContainer = this.$refs.gameContainer;
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        },
        handleRoundFinishedCalculation(roundIndex) {
            const scores = [ ...this.currentGame.rounds[roundIndex].scores ];
            const scoresFromFirst = scores.sort((a, b) => a.index > b.index);
            
            let winningScore = scoresFromFirst[0];
            let impaledEvent = null;

            for(let i = 0; i < scoresFromFirst.length; i++) {
                const score = scoresFromFirst[i];
                if(i > 0 && winningScore.score === score.score) impaledEvent = {
                    userId: winningScore.userId,
                    impaledByUserId: score.userId,
                    score: score.score
                }
                if(score.score <= winningScore.score) winningScore = score;
                if(impaledEvent && winningScore.score < impaledEvent.score) impaledEvent = null;
            }
            
            if(impaledEvent) {
                this.updateUserRoundScore(impaledEvent.userId, 30, { impaled: true, impaledByUserId: impaledEvent.impaledByUserId });
                this.recalculateUserDisplayScore(impaledEvent.userId);
                this.setCircleAnimation(impaledEvent.userId);
            }

            if(impaledEvent?.userId !== winningScore.userId) {
                const settings = winningScore.settings || {};
                let score = 0;
                if(settings.finishWithZero) score = winningScore.score;

                this.updateUserRoundScore(winningScore.userId, score, { ...settings, winner: true });
                this.recalculateUserDisplayScore(winningScore.userId);
                this.setCircleAnimation(winningScore.userId);
            }

            const updatedScores = [ ...this.currentGame.rounds[roundIndex].scores ];
            const updatedScoresFromFirst = updatedScores.sort((a, b) => a.index > b.index);

            for(let i = 0; i < updatedScoresFromFirst.length; i++) {
                const score = updatedScoresFromFirst[i];
                const user = this.currentGame.users.find(user => user.id === score.userId);
                const isReset = this.resetCheckpoints.includes(user.displayScore) && score.score > 0;
                const previousScore = user.displayScore - score.score;
                const nextCheckpoints = this.shotCheckpoints.filter(checkpointScore => checkpointScore > previousScore);
                const passedCheckpoints = nextCheckpoints.filter(checkpointScore => checkpointScore <= user.displayScore);
                if(passedCheckpoints.length > 0 || isReset) {
                    const settings = score.settings;
                    if (passedCheckpoints.length > 0) settings['shotsTaken'] = passedCheckpoints.length;
                    if (isReset) settings['shotsTaken'] = settings['shotsTaken'] + 1;
                    this.updateUserRoundScore(score.userId, score.score, settings);
                }
            }
        },
        getResetScore(score) {
            if(score === 50) return 0
            if(score === 69) return 50
            if(score === 75) return 50
            if(score === 100) return 50
        },
        handleRoundSelected(round) {
            this.setScrollTop();
            this.scrollToRound(round);
            this.currentRoundNumber = round;
            this.isInLastRound = round === this.currentGame.rounds.length;
            this.scrollToLastTop();
        },
        _handleScroll: _.debounce(function(event) {
            if(this.handleScrollDisabled) return

            const roundWidth = 100;
            const gap = 20;
            const scroll = event.target.scrollLeft;

            const focusedRound = Math.round(scroll / ((roundWidth + gap)) + 1);
            if(this.currentRoundNumber === focusedRound) return

            this.currentRoundNumber = focusedRound;
            this.isInLastRound = focusedRound === this.currentGame.rounds.length;
            this.scrollToLastTop();
        }, 1),
        _handleContainerScroll: _.debounce(function(event) {
            this.setScrollTop();
        }, 200),
        setScrollTop() {
            const firstUser = this.roundUsers[0];
            const element = document.getElementById(`user-${firstUser.id}`);
            const { top } = element.getBoundingClientRect();
            this.lastScrollTop = top;
        },
        scrollToLastTop() {
            this.$nextTick(() => {
                const container = this.$refs.gameContainer;
                const firstUser = this.roundUsers[0];
                const element = document.getElementById(`user-${firstUser.id}`);
                const top = element.offsetTop - this.lastScrollTop;
                container.scrollTo({ top })
            })
        },
        // async updateCircleColor(userId) {
        //     await new Promise(r => setTimeout(r, 1));
        //     const roundScores = this.currentGame.rounds.map(round => {
        //         if(round.scores.length === 0) return 0
        //         const roundScoreIndex = round.scores.findIndex(score => score.userId === userId);
        //         let roundScore = 0;
        //         if(roundScoreIndex !== -1) roundScore = round.scores[roundScoreIndex].score
        //         return roundScore
        //     });
        //     const circleElement = document.getElementById(`outer-circle-${userId}`);
        //     circleElement.style = `--background-style: ${this.getBackgroundStyle(roundScores)}`;
        // },
        async setAllCircles() {
            await new Promise(r => setTimeout(r, 1));
            const userIds = this.currentRound.userIds;
            const stagger = 20;
            for(let i = 0; i < userIds.length; i++) {
                this.setCircleAnimation(userIds[i], true);
                await new Promise(r => setTimeout(r, stagger));
            }
        },
        updateAllCircles(force) {
            this.$nextTick(() => {
                const users = this.currentGame.users;
                for(let i = 0; i < users.length; i++) {
                    const reset = users[i].lastDisplayScore === undefined;
                    this.setCircleAnimation(users[i].id, reset || force);
                }
            })
        },
        setCircleAnimation(userId, restart = false) {
            const userIndex = this.currentRound.userIds.findIndex(roundUserId => roundUserId === userId);
            let roundScores = [];
            let lastScoreResetIndex = 0;

            for(let i = 0; i < this.currentGame.rounds.length; i++) {
                const round = this.currentGame.rounds[i];
                const roundScoreIndex = round.scores.findIndex(score => score.userId === userId);
                let roundScore = 0;
                let hasJP0 = false;
                if(roundScoreIndex !== -1) {
                    roundScore = round.scores[roundScoreIndex].score || 0;
                    hasJP0 = round.scores[roundScoreIndex].settings.finishWithZero;
                }

                // only show scores relevant to current round
                if(round.scores.length === 0 || (lastScoreResetIndex > this.currentRoundNumber-1)) continue
                roundScores.push(roundScore);
                
                const accumulatedScore = eval(roundScores.join('+'));
                
                if(!this.resetCheckpoints.includes(accumulatedScore) && !hasJP0) continue
                
                let startingScore = 0;
                if(hasJP0) startingScore = accumulatedScore;
                else startingScore = this.getResetScore(accumulatedScore);

                let resetScore = false;
                if(hasJP0) resetScore = i <= this.currentRoundNumber-1;
                else resetScore = i < this.currentRoundNumber-1;

                if(resetScore) {
                    roundScores = roundScores.map(() => 0);
                    roundScores[roundScores.length-1] = startingScore;
                }

                if(hasJP0) lastScoreResetIndex = i;
                else lastScoreResetIndex = i+1;
            }

            let oldScores = this.currentGame.users[userIndex].oldScores || [];
            let newScores = roundScores;

            if(restart) oldScores = roundScores.map(() => 0);
            else {
                while(oldScores.length < newScores.length) { oldScores.push(0); };
                while(newScores.length < oldScores.length) { newScores.push(0); };
            }
            
            this.currentGame.users[userIndex].oldScores = oldScores;
            this.currentGame.users[userIndex].newScores = newScores;

            this.setRoundUsers();
            this.increaseRoundDegrees(userId);
        },
        async increaseRoundDegrees(userId) {
            const userIndex = this.currentRound.userIds.findIndex(roundUserId => roundUserId === userId);

            let oldScores = this.currentGame.users[userIndex].oldScores;
            let newScores = this.currentGame.users[userIndex].newScores;

            const newRoundScore = newScores.length > oldScores.length;

            if(!oldScores.length) oldScores = [];

            this.currentGame.users[userIndex].oldScores = newScores;
            this.setRoundUsers();

            const steps = 80;
            for(let i = 0; i < steps; i++) {
                const timeline = (i+1) / steps;
                const curvedStep = this.easeInOut(timeline);
                const increasedScores = oldScores.map((oldScore, index) => oldScore + ((newScores[index] - oldScore) * curvedStep));
                const circleElement = document.getElementById(`outer-circle-${userId}`);
                const backgroundStyle = this.getBackgroundStyle(increasedScores, newRoundScore, i+1);
                circleElement.style = `--background-style: ${backgroundStyle}`;
                await new Promise(r => setTimeout(r, 1));
            }
        },
        easeInOut(x) {
            return x < 0.5
                ? (1 - Math.sqrt(1 - Math.pow(2 * x, 2))) / 2
                : (Math.sqrt(1 - Math.pow(-2 * x + 2, 2)) + 1) / 2;
        },
        getBackgroundStyle(incrementScores = [], newRoundScore = false, iteration) {
            if(incrementScores.length === 0) return '#335f36'
            
            let currentDegrees = 0;
            const totalScore = eval(incrementScores.join('+'));
            const maxScore = totalScore === 100;
            if(!maxScore && totalScore !== 0) incrementScores.push(100 - totalScore);

            let gradients = [];
            
            incrementScores.forEach((score, index) => {
                if(score === 0) return

                const defaultLineDegrees = 5;
                const totalLineDegrees = (incrementScores.length) * defaultLineDegrees;
                const scoreDegrees = 360 * (score / 100) - (totalLineDegrees / (incrementScores.length));
                let roundColor = '#ffffff';
                const currentRound = index === this.currentRoundNumber-1;
                if(currentRound) roundColor = '#4B9E09';
                let seperatorColor = '#335f36';
                const isLastIndex = index === incrementScores.length-1;
                if(!maxScore && isLastIndex) roundColor = '#335f36';

                let lineWidth = defaultLineDegrees;
                if((incrementScores.length === 1 || isLastIndex) && newRoundScore) lineWidth = defaultLineDegrees / 30 * iteration;

                const firstSeperationLine = `${seperatorColor} ${currentDegrees}deg ${currentDegrees + (lineWidth / 2)}deg`;
                currentDegrees += lineWidth / 2;
                
                const scoreBar = `${roundColor} ${currentDegrees}deg ${currentDegrees + scoreDegrees}deg`;
                currentDegrees += scoreDegrees;

                const lastSeperationLine = `${seperatorColor} ${currentDegrees}deg ${currentDegrees + (lineWidth / 2)}deg`;
                currentDegrees += lineWidth / 2;

                gradients.push(`${firstSeperationLine}, ${scoreBar}, ${lastSeperationLine}`);
            })

            if(gradients.length === 0) return '#335f36'
            
            const gradient = gradients.join(', ');

            return `conic-gradient(${gradient});`
        },
        handleUserDragFinished() {
            this.enableDragUsers = false;
            this.gameMessage = null;

            this.startRound();
            this.setAllCircles();
            this.scrollToRound(this.currentGame.rounds.length, true);
        },
        handleMouseDown(element) {
            const popupElement = this.$refs.scorePopup;
            if(!this.showScorePopup || popupElement.contains(element) || this.scrolling) return

            this.showScorePopup = false;
            this.editScoreUserId = null;
            const roundScores = this.currentGame.rounds[this.currentRoundNumber-1].scores;
            const userIds = this.currentRound.userIds;

            if(roundScores.length === userIds.length && this.currentGame.rounds.length === this.currentRoundNumber) this.handleFinishRound();
        },
        handlePageScroll(event) {
            const scrollContainer = this.$refs.gameContainer;
            if(scrollContainer.contains(event.target)) this.scrolling = true;
            else this.scrolling = false;
            this._resetScrolling();
        },
        _resetScrolling: _.debounce(function() {
            this.scrolling = false;
        }, 100),
        handleEmitOrderChange() {
            const userIds = this.roundUsers.map(user => user.id);
            this.$emit('userOrderUpdated', userIds);
        },
        handleBackNavigation(event) {
            if(this.showScorePopup) {
                this.editScoreUserId = null;
                this.showScorePopup = false;
            } else this.$emit('back');
        },
        _setUserRoundScores: _.debounce(function() {
            this.setUserRoundScores();
        }, 200),
        setUserRoundScores() {
            if(this.currentRoundNumber === this.currentGame.rounds.length) return this.userRoundScores = {}

            const scores = {};
            for(let i = 0; i < this.roundUsers.length; i++) {
                const user = this.roundUsers[i];
                const userRound = this.currentRound.scores.find(score => score.userId === user.id);
                if (!userRound) return;
                
                let score = 0;
                if(userRound) score = userRound.score;
                const reset = this.resetCheckpoints.includes(user.displayScore);

                let shots = userRound?.settings?.shotsTaken || 0;

                scores[user.id] = {
                    score,
                    winner: userRound.settings.winner,
                    impaled: userRound.settings.impaled,
                    reset,
                    shots,
                }
            }
            this.userRoundScores = scores;
        },
        getSurpassedShotCheckpoints(score) {
            return this.shotCheckpoints.filter(checkpoint => checkpoint <= score).length
        },
        _setRoundUsers: _.debounce(function() {
            this.setRoundUsers();
        }, 200),
        setRoundUsers() {
            const round = this.currentRound;
            const userIds = round.userIds;
            const roundUsers = userIds.map((userId, index) => {
                const gameUser = this.currentGame.users.find(user => user.id === userId);
                const { hasAvatar = false, base64avatar } = this.users.find(user => user.id === userId) || {};

                return {
                    ...gameUser,
                    hasAvatar,
                    base64avatar,
                    index
                }
            });
            this.roundUsers = roundUsers;
        },
        async handleManagePlayers() {
            if(this.currentRoundNumber !== this.currentGame.totalRounds) {
                this.scrollToRound(this.currentGame.totalRounds);
                await new Promise(r => setTimeout(r, 1000));
            }
            this.enableDragUsers = true;
        },
        setCurrentRound() {
            const rounds = this.currentGame.rounds;
            let currentRoundNumber = this.currentRoundNumber-1;
            if(currentRoundNumber === -1) currentRoundNumber = this.currentGame.rounds.length-1;
            const round = rounds[currentRoundNumber] || rounds[this.currentGame.totalRounds-1];
            this.currentRound = round;
        },
        _updateCurrentGame: _.debounce(function() {
            this.setCurrentRound();
            this.recalculateUsersDisplayScores();
            this.updateAllCircles();
            this.setUserRoundScores();
        }, 200),
        handleShowAnimation() {
            let you = localStorage.getItem('you');
            if (!you) return;
            you = JSON.parse(you);

            const finishedRound = this.currentGame.rounds[this.currentGame.rounds.length-2];
            const yourScore = finishedRound.scores.find(score => score.userId === you.id);
            const shotsTaken = yourScore.settings?.shotsTaken || 0;
            if (shotsTaken) this.shotsTaken = shotsTaken;
        },
        handleWindowFocus() {
            this.scrollToRound(this.lastActiveRound);
        }
    },
    computed: {
        canStartNextRound: function() {
            if(!this.currentGame) return false

            const isLastRound = this.currentRoundNumber === this.currentGame.totalRounds;
            return isLastRound && this.currentRound.scores.length === this.currentRound.userIds.length
        },
        lastActiveRound: function() {
            let lastActiveRound = 0;
            for(let i = 0; i < this.currentGame.rounds.length; i++) {
                if(this.currentGame.rounds[i].scores.length > 0) lastActiveRound = i+1;
            }
            return lastActiveRound
        },
        editingUserRoundScore: function() {
            if(!this.editScoreUserId) return null
            const gameRounds = this.currentGame.rounds;
            const round = gameRounds[this.currentRoundNumber-1];
            if(!round) return null
            const userScore = round.scores.find(score => score.userId === this.editScoreUserId);
            if(!userScore) return null
            return userScore.score
        }
    },
    created() {
        const game = {
            ...this.game,
            totalRounds: this.game.rounds.length
        };

        this.startGame(game);

        this.$root.$on('popstate', this.handleBackNavigation);
    },
    mounted() {
        const vm = this;
        this.$root.$on('clickElement', vm.handleMouseDown);
        this.$root.$on('targetElementMoved', vm.handlePageScroll);
        this.$root.$on('managePlayerOrder', vm.handleManagePlayers);
        this.$root.$on('windowfocus', this.handleWindowFocus);
        this.setScrollTop();

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('shot_animation')) {
            this.shotsTaken = parseFloat(this.$route.query['shot_animation']);

            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    },
    beforeDestroy() {
        const vm = this;
        this.$root.$off('clickElement', vm.handleMouseDown);
        this.$root.$off('targetElementMoved', vm.handlePageScroll);
        this.$root.$off('managePlayerOrder', vm.handleManagePlayers);
        this.$root.$off('windowfocus', this.handleWindowFocus);
        this.$root.$off('popstate', this.handleBackNavigation);
    },
    watch: {
        currentRoundNumber: function(newVal, oldVal) {
            if(oldVal === undefined || oldVal === 0) return
            
            this._updateCurrentGame();
        },
        game: {
            handler(after, before) {
                console.log('GAME WATCHED, hasChanges:', this.hasChanges)
                const beforeUsers = this.currentGame.users;
                let users = after.users;

                // if(this.canStartNextRound && !this.hasChanges) return this.handleFinishRound(true);

                if (!this.hasChanges) {
                    users = users.map(afterUser => {
                        const beforeUser = beforeUsers.find(beforeUser => beforeUser.id === afterUser.id);
                        afterUser.oldScores = beforeUser?.oldScores || [];
                        afterUser.newScores = beforeUser?.newScores || [];
                        return afterUser;
                    });

                    if (this.currentRoundNumber === this.currentGame.rounds.length - 1 && this.currentGame.rounds[this.currentRoundNumber].scores.length > 0) this.scrollToRound(this.currentRoundNumber+1);
                };

                const game = {
                    ...after,
                    users,
                    totalRounds: after.rounds.length
                };
                this.currentGame = _.cloneDeep(game);

                this._updateCurrentGame();
                
                this.cachedRounds = game.rounds;
            }, deep: true
        },
        'game.rounds.length'() {
            const newRoundIntroduced = !this.game.rounds[this.game.rounds.length-1].scores?.length;
            if (!newRoundIntroduced) return;

            this.handleShowAnimation();

            if (!this.hasChanges) this.scrollToRound(this.game.rounds.length-1);
        },
        showScorePopup: function() {
            if(this.currentRoundNumber === this.currentGame.totalRounds) return
            
            this.handleRoundFinishedCalculation(this.currentRoundNumber-1);
            this._updateCurrentGame();
        },
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';

.page-wrapper {
    height: 100%;

    .game-wrapper {
        height: calc(100% - 80px);

        .center {
            position: absolute;
            top: 0;
            width: var(--absolute-width);
            height: 100dvh;
            isolation: isolate;

            &:after {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                width: var(--absolute-width);
                height: 100dvh;
                background-color: #00000000;
                transition: .3s ease-out;
                pointer-events: none;
                z-index: -1;
            }

            &.move-up {
                &:after {
                    background-color: #00000030;
                }
                .game-container {
                    max-height: calc(100dvh - 288px - 80px);
                    .overflow-container {
                        padding-bottom: 288px;
                        transition: unset;
                    }
                }
            }

            .game-container {
                padding-top: 80px;
                min-height: calc(100dvh - 80px);
                max-height: calc(100dvh - 80px);
                overflow-y: auto;

                .overflow-container {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    gap: 40px;
                    padding-bottom: 80px;
                    transition: 400ms cubic-bezier(.7,.05,.31,.95);

                    .top {
                        position: absolute;
                        top: 100px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        width: calc(100% - 40px);
                        padding-inline: 20px;
                        z-index: 1;
    
                        .message {
                            color: #f1f1f1;
                            text-align: center;
                            text-shadow: 0 0 8px #132914;
                        }
                    }
    
                    .users-container {
                        display: flex;
                        flex-direction: column;
                        justify-content: space-around;
                        min-height: calc(100dvh - 80px - 80px);
                        transition: 400ms cubic-bezier(.7,.05,.31,.95);
    
                        .users-wrapper {
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: space-around;
                            gap: 40px 0;
                            padding-block: 40px;
        
                            &.disable-drag {
                                pointer-events: none;
                            }
        
                            .grid-item {
                                display: grid;
                                place-items: center;
                                width: 50%;    
                            }
                        }
                    }
                }
            }
        }
        .bottom {
            position: absolute;
            bottom: 0px;
            inset-inline: 0;
            padding-top: 40px;
            background: linear-gradient(0deg, #132914 0%, #132914 20%, #13291400);
            z-index: 1;
            pointer-events: none;

            .rounds-container {
                position: relative;
                --round-width: 100px;

                .rounds-scroll-bar {
                    scroll-behavior: smooth;
                    overflow-x: scroll;
                    overflow-y: hidden;
                    max-width: var(--absolute-width);
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                    scroll-snap-type: x mandatory;
                    overscroll-behavior: none;

                    display: flex;
                    gap: 20px;

                    &::-webkit-scrollbar {
                        display: none;
                    }

                    &.no-snap {
                        scroll-snap-type: none;
                    }

                    .round {
                        min-width: var(--round-width);
                        text-align: center;
                        scroll-snap-align: center;
                        color: #bebebe;
                        font-weight: 300;
                        transition: color, font-weight .2s ease;
                        padding-top: 16px;
                        padding-bottom: 26px;
                        pointer-events: all;

                        p {
                            font-size: 18px;
                        }
                        
                        &.active {
                            color: #f1f1f1;
                            font-weight: 600 !important;
                        }
                        &:first-of-type {
                            padding-left: calc((var(--absolute-width) / 2) - (var(--round-width) / 2));
                            transition: .2s ease;
                        }
                        &:last-of-type {
                            padding-right: calc((var(--absolute-width) / 2) - (var(--round-width) / 2));
                            transition: .2s ease;
                        }
                    }
                }

                .line {
                    position: absolute;
                    width: var(--round-width);
                    height: 1px;
                    background-color: #f1f1f1;
                    left: 50%;
                    bottom: 18px;
                    translate: -50% 0;
                    border-radius: 2px;
                    box-shadow: 0 -4px 20px 2px #ffffff40;
                }
            }

            .button-container {
                position: absolute;
                bottom: 20px;
                inset-inline: calc(var(--absolute-width) / 10);
                pointer-events: all;
            }
        }

        .add-score-popup {
            position: absolute;
            inset-inline: calc(var(--absolute-width) / 20);
            bottom: -40px;
            --padding-inline: calc(var(--absolute-width) / 100 * 6);
            padding: 20px var(--padding-inline) 56px var(--padding-inline);
            border-radius: 32px 32px 0 0;
            background-color: #1b2d1f;
            opacity: 0;

            display: flex;
            flex-direction: column;
            gap: 16px;

            transform: translate(0, 100%) scale(0.5);
            -webkit-transform: translate(0, 100%) scale(0.5);
            transform-origin: center top;
            pointer-events: none;
            transition: transform .5s ease 150ms, opacity .35s ease 150ms;
            -webkit-transition: transform .5s ease 150ms, opacity .35s ease 150ms;

            z-index: 10;

            &.show {
                opacity: 1;
                transform: translate(0, 0) scale(1);
                -webkit-transform: translate(0, 0) scale(1);
                pointer-events: all;
                transition: .4s cubic-bezier(.7,.05,.31,.95) .15s;
                -webkit-transition: .4s cubic-bezier(.7,.05,.31,.95) .15s;
            }

            .text {
                display: flex;
                flex-direction: column;
                gap: 4px;

                p {
                    text-align: center;
                    color: #f1f1f1;
                    font-size: 16px;
                }
    
                .editing-user {
                    font-weight: 500;
                }
    
                .tip {
                    color: #e8e8e8;
                    font-size: 12px;
                }
            }

            .input-field {
                display: flex;
                gap: 12px;
                width: 100%;

                .custom-input {
                    width: calc(66.6% - 1.5em);
                    background: #f1f1f1;
                    font-size: 16px;
                    border-radius: 1.5em;
                    padding: 0.5em 0.75em;
                    border: none;
                    min-height: 16px;
                }

                .backspace-button {
                    width: calc(33.3% - 12px);
                    height: 40px;
                    padding: 0;
                    font-size: 30px;
                    line-height: 30px;

                    .backspace-icon {
                        width: 40px;
                        height: 40px;
                        fill: #34422f;
                    }
                }
            }

            .inputs-wrapper {
                align-items: center;

                .row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    width: 100%;

                    .item {
                        flex: 1 1 0px;

                        &.jump-in {
                            &.hide {
                                transform: scale(0);
                                margin-inline: -16px;
                                transition: .2s cubic-bezier(.7,.05,.31,.95);
                                flex: 0 1 0px;
                            }
                            &.show {
                                transform: scale(1);
                                margin-inline: 0;
                                transition: .3s cubic-bezier(.51,1.4,.45,1.08);
                            }
                        }

                        .number-button {
                            width: 100%;
                            height: 40px;
                            padding: 0;
                            font-size: 22px;
                        }
                        .text-button {
                            width: 100%;
                            height: 40px;
                            padding: 0;
                            font-size: 18px;
                        }
                        .icon-button {
                            display: grid;
                            place-items: center;
                            width: 100%;
                            height: 40px;
                            padding: 0;

                            .icon {
                                width: 26px;
                                height: 26px;
                                transform: rotate(0deg);
                                transition: .3s cubic-bezier(.7,.05,.31,.95);
                                -webkit-transition: .3s cubic-bezier(.7,.05,.31,.95);

                                &.previous.rotate-down {
                                    transform: rotate(-90deg);
                                }
                                &.next.rotate-down {
                                    transform: rotate(90deg);
                                }
                            }
                        }
                        .select-button {
                            button {
                                width: 100%;
                                height: 40px !important;
                                padding: 0 !important;
                                margin: 0;
                                font-size: 22px;
                            }
                        }
                    }
                }
            }

            .buttons {
                display: flex;
                justify-content: space-between;

                .button {
                    width: calc(50% - 6px);
                }
            }
        }
    }
}

</style>
